(() => {
  if (customElements.get('pe-carousel')) return;
  class PeCarousel extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.querySelectorAll('[data-prev],[data-next]').forEach(button => {
        const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        arrow.setAttribute('viewBox', '0 0 14 10');
        arrow.setAttribute('class', 'pe-control__icon');
        arrow.setAttribute('width', '20');
        arrow.setAttribute('height', '20');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.setAttribute('focusable', 'false');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('fill', 'currentColor');
        path.setAttribute('fill-rule', 'evenodd');
        path.setAttribute('clip-rule', 'evenodd');
        path.setAttribute('d', 'M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546');
        arrow.append(path);
        button.replaceChildren(arrow);
      });
      const status = this.querySelector('[data-status]');
      const controls = this.querySelector('.pe-products__footer .pe-controls');
      const next = controls?.querySelector('[data-next]');
      if (status && next) {
        status.setAttribute('dir', 'ltr');
        controls.insertBefore(status, next);
      }
      this.index = 0;
      this.paused = false;
      this.visible = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const signal = this.abort.signal;
      this.querySelectorAll('[data-prev],[data-next],[data-dot],[data-pause]').forEach(button => {
        button.hidden = false;
        button.addEventListener('click', () => {
          if (button.hasAttribute('data-pause')) {
            this.paused = !this.paused;
            button.setAttribute('aria-pressed', String(this.paused));
            button.textContent = this.paused ? this.dataset.play : this.dataset.pause;
            this.schedule();
          } else {
            this.go(button.hasAttribute('data-dot') ? Number(button.dataset.dot) : this.index + (button.hasAttribute('data-next') ? 1 : -1));
            this.schedule();
          }
        }, { signal });
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { passive: true, signal });
      this.addEventListener('mouseenter', () => clearTimeout(this.timer), { signal });
      this.addEventListener('mouseleave', () => this.schedule(), { signal });
      this.addEventListener('focusin', () => clearTimeout(this.timer), { signal });
      this.addEventListener('focusout', () => this.schedule(), { signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.go(this.index + ((event.key === 'ArrowLeft') === rtl ? 1 : -1));
      }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: .2 });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.track);
      this.update();
    }
    setItems(fragment) {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.track.replaceChildren(fragment);
      this.slides = [...this.track.children];
      this.index = 0;
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
      this.schedule();
    }
    go(index) {
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides[index]?.scrollIntoView({ block: 'nearest', inline: 'start', behavior: this.reduced ? 'instant' : 'smooth' });
    }
    update() {
      const rtl = getComputedStyle(this).direction === 'rtl';
      this.dataset.direction = rtl ? 'rtl' : 'ltr';
      const bounds = this.track.getBoundingClientRect();
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rtl ? bounds.right - rect.right : bounds.left - rect.left);
        if (delta < distance) { distance = delta; this.index = index; }
      });
      const finalRect = this.slides[this.slides.length - 1]?.getBoundingClientRect();
      const atEnd = finalRect && (rtl ? finalRect.left >= bounds.left - 2 : finalRect.right <= bounds.right + 2);
      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
      const footer = this.querySelector('.pe-products__footer');
      if (footer) footer.hidden = !hasOverflow;
      this.querySelectorAll('[data-prev]').forEach(button => {
        button.hidden = !hasOverflow;
        button.disabled = !hasOverflow || this.index === 0;
      });
      this.querySelectorAll('[data-next]').forEach(button => {
        button.hidden = !hasOverflow;
        button.disabled = !hasOverflow || Boolean(atEnd);
      });
      this.querySelectorAll('[data-dot]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.dot) === this.index)));
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = (this.slides.length ? this.index + 1 : 0) + ' / ' + this.slides.length;
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.dataset.autoplay || this.paused || !this.visible || this.reduced || document.hidden || this.matches(':focus-within,:hover') || this.slides.length < 2) return;
      this.timer = setTimeout(() => { this.go((this.index + 1) % this.slides.length); this.schedule(); }, Number(this.dataset.autoplay) * 1000);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect(); clearTimeout(this.timer); cancelAnimationFrame(this.frame);
    }
  }
  customElements.define('pe-carousel', PeCarousel);
})();



